import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  ImportOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Image as AntdImage,
  Button,
  Empty,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  SpinProps,
  Typography,
} from "antd";
import { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { usePictureData } from "../contexts/PictureDataProvider";
import { Filter } from "../shared/protocols/editor/PtlDataFind";
import { fileInput, Flexed, Important } from "./common";

const { Title } = Typography;

interface PictureFallsProps {
  title?: string;
  columnWidth?: number;
  columnCount?: number;
  selected?: string;
  allowImport?: boolean;
  allowRename?: boolean;
  allowReplace?: boolean;
  allowDelete?: boolean;
  allowSelect?: boolean;
  onSelect?: (id?: string) => void;
}

const Header = styled(Flex)`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 16px;
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    padding: 10px;

    > :nth-child(1) {
      flex: 1 1 auto !important;
    }

    > :nth-child(2) {
      flex: 1 0 100% !important;
      order: 3;
    }
  }
`;

const Container = styled.div<{ $columnCount: number }>`
  width: 100%;
  padding: 10px;
  column-count: ${(p) => p.$columnCount};
  column-gap: 16px;

  @media (max-width: 600px) {
    padding: 8px;
    column-gap: 10px;
  }
`;

const PictureContainer = styled.div<{ "$data-selected": boolean }>`
  display: inline-block;
  width: 100%;
  break-inside: avoid;
  background-color: #fff;
  background-position: 0 0, 0 20px, 20px -20px, -20px 0px;
  box-shadow: ${(props) =>
    props["$data-selected"]
      ? "0 0 16px #1677ff"
      : "0 0 8px rgba(0, 0, 0, 0.2)"};
  border: ${(props) => (props["$data-selected"] ? "2px solid #1677ff" : "")};
  padding: 8px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
  transition: box-shadow 0.2s ease-in-out;
  will-change: box-shadow;
  & .image {
    display: inline-block;
  }
  &:hover {
    box-shadow: ${(props) =>
      props["$data-selected"]
        ? "0 0 16px #1677ff"
        : "0 0 16px rgba(0, 0, 0, 0.5)"};
  }
`;

const Image = styled(AntdImage)`
  background-color: white;
  background-image: linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 40px 40px;
  background-position: 0 0, 0 20px, 20px -20px, -20px 0px;
`;

const PictureName = styled.div`
  max-width: calc(100% - 4em);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default function PictureFalls({
  title,
  columnWidth,
  columnCount,
  selected,
  allowImport = false,
  allowRename = false,
  allowReplace = false,
  allowDelete = false,
  allowSelect = false,
  onSelect,
}: PictureFallsProps) {
  const [modal, contextModal] = Modal.useModal();
  const [spin, setSpin] = useState<SpinProps>({ spinning: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(columnCount || 4);
  const [filter, setFilter] = useState<Filter>({});
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [editingName, setEditingName] = useState<string | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [needLoad, setNeedLoad] = useState(0);

  const { data, urls, load, upload, replace, rename, $delete } =
    usePictureData();

  useEffect(() => setSelectedId(selected), [selected]);

  useEffect(() => {
    if (columnCount) {
      setColumns(columnCount);
      return;
    }
    if (!columnWidth) return;

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = e.contentRect.width;
        setColumns(Math.max(1, Math.floor(w / columnWidth)));
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [columnWidth, columnCount]);

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setNeedLoad((prev) => prev + 1);
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );
    setTimeout(() => {
      obs.observe(el);
    }, 1000);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (needLoad) load(filter);
  }, [needLoad]);

  useEffect(() => {
    requestAnimationFrame(() => {
      load(filter, true);
    });
  }, [JSON.stringify(filter)]);

  return (
    <>
      <Header justify="space-between" gap={6}>
        <Flexed as={Title} $flex={6} level={5}>
          {title}
        </Flexed>
        <Flexed
          as={Input.Search}
          $flex={12}
          allowClear
          onSearch={(v) =>
            setFilter({
              $expr: {
                $regexMatch: {
                  input: { $concat: ["$name", "$ext"] },
                  regex: v,
                  options: "i",
                },
              },
            })
          }
        />
        <Flex gap={6}>
          {allowImport && (
            <Button
              onClick={async () => {
                setSpin({ spinning: true, tip: "上传中..." });
                const res = await upload(await fileInput({}));
                const Info: FC<{ data: string[]; action: string }> = ({
                  data,
                  action,
                }) => (
                  <div>
                    <p>
                      <Important>{data.length}</Important> 个文件{action}：
                    </p>
                    {data.length > 0 && <p>{data.join("，")}</p>}
                  </div>
                );
                modal.info({
                  title: "上传结果",
                  okText: "确定",
                  centered: true,
                  content: (
                    <>
                      <Space direction="vertical">
                        <Info data={res?.succeed ?? []} action="上传成功" />
                        {res.errored.length > 0 && (
                          <Info data={res.errored} action="上传失败" />
                        )}
                        {res.replaced.length > 0 && (
                          <Info data={res.replaced} action="被覆盖" />
                        )}
                      </Space>
                    </>
                  ),
                });
                await load(filter, true);
                setSpin({ spinning: false });
              }}
            >
              <ImportOutlined /> 导入
            </Button>
          )}
        </Flex>
      </Header>
      {data.length === 0 && <Empty style={{ marginTop: 40 }} />}
      <Container ref={containerRef} $columnCount={columns}>
        {data.map((item, index) => (
          <PictureContainer
            key={index}
            $data-selected={allowSelect && item._id === selectedId}
            onClick={() => {
              if (!allowSelect) return;
              const id = item._id === selectedId ? undefined : item._id;
              onSelect!(id);
              setSelectedId(id);
            }}
          >
            <Image
              src={urls[item._id]}
              alt={item.name}
              className="image"
              preview={!allowSelect}
            />
            <Flex
              justify="space-between"
              align="center"
              style={{ marginTop: 8 }}
            >
              {editingId === item._id ? (
                <Input
                  allowClear
                  maxLength={20}
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                />
              ) : (
                <Flex
                  style={{
                    width: `calc(100% - ${
                      [allowRename, allowReplace, allowDelete].filter((v) => v)
                        .length * 32
                    }px)`,
                  }}
                >
                  <PictureName>{item.name}</PictureName>
                  {item.ext}
                </Flex>
              )}
              <Flex>
                {allowRename && editingId !== item._id && (
                  <Button
                    disabled={!!editingId && editingId !== item._id}
                    type="link"
                    style={{ width: 20 }}
                    onClick={() => {
                      setEditingId(item._id);
                      setEditingName(item.name);
                    }}
                  >
                    <EditOutlined />
                  </Button>
                )}
                {allowReplace && editingId !== item._id && (
                  <Button
                    disabled={!!editingId && editingId !== item._id}
                    type="link"
                    style={{ width: 20 }}
                    onClick={async () =>
                      replace(item, (await fileInput({ multiple: false }))[0])
                    }
                  >
                    <UploadOutlined />
                  </Button>
                )}
                {allowDelete && editingId !== item._id && (
                  <Popconfirm
                    title={
                      <p>
                        确认删除<Important>{item.name}</Important>
                      </p>
                    }
                    okText="确定"
                    cancelText="取消"
                    onConfirm={async () => $delete(item)}
                  >
                    <Button
                      disabled={!!editingId && editingId !== item._id}
                      danger
                      type="link"
                      style={{ width: 20 }}
                      onClick={() => {
                        setEditingId(undefined);
                        setEditingName(undefined);
                      }}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                )}
                {editingId === item._id && (
                  <Button
                    type="link"
                    style={{ width: 20 }}
                    onClick={() => {
                      if (!editingName || editingName.trim().length === 0)
                        return;
                      rename(editingId, editingName!.trim()).then(() => {
                        setEditingId(undefined);
                        setEditingName(undefined);
                      });
                    }}
                  >
                    <CheckOutlined />
                  </Button>
                )}
                {editingId === item._id && (
                  <Button
                    type="link"
                    style={{ width: 20 }}
                    onClick={() => {
                      setEditingId(undefined);
                      setEditingName(undefined);
                    }}
                  >
                    <CloseOutlined />
                  </Button>
                )}
              </Flex>
            </Flex>
          </PictureContainer>
        ))}
        <div ref={loaderRef} style={{ breakInside: "avoid" }} />
      </Container>
      {contextModal}
      <Spin {...spin} fullscreen />
    </>
  );
}
